/**
 * Rich Matching Player
 * Layout pass-through: renders tabs/stacked/split layout with PagePlayer and MatchPlayer.
 * All grading props flow through from ContentPlayer.
 */

import * as React from 'react';
import {
    cn,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '@ai-content-hub/content-kit';
import type { GradingResult } from '@ai-content-hub/content-kit';
import { isCorrectAnswerRevealed, useLocale, ContentPlayerSplitLayout } from '@ai-content-hub/content-core';
import {
    PagePlayer,
    MatchPlayer,
    type MatchAnswer,
} from '@ai-content-hub/content-elements';
import type { RichMatchingData, RichMatchingConfig, PlayerViewLayout } from './schema';
import { defaultRichMatchingData } from './schema';
import { BookOpen, Shuffle } from 'lucide-react';

type TabValue = 'lesson' | 'questions';

export interface RichMatchingPlayerProps {
    data?: RichMatchingData;
    value?: RichMatchingData;
    config?: RichMatchingConfig;
    answer?: MatchAnswer;
    onAnswer?: (answer: MatchAnswer) => void;
    isReview?: boolean;
    disabled?: boolean;
    showFeedback?: boolean;
    result?: GradingResult;
    className?: string;
}


export const RichMatchingPlayer: React.FC<RichMatchingPlayerProps> = ({
    data: dataProp,
    value: valueProp,
    config,
    answer,
    onAnswer,
    isReview = false,
    disabled = false,
    showFeedback = false,
    result,
    className,
}) => {
    const data = dataProp ?? valueProp;
    const [activeTab, setActiveTab] = React.useState<TabValue>('lesson');
    const { t } = useLocale({ config: config?.i18n });
    // ELMS-2370: LMS "hide answer" => behavior.showCorrectAnswer === false.
    const revealCorrect = isCorrectAnswerRevealed(config);

    const safeData: RichMatchingData = {
        ...defaultRichMatchingData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultRichMatchingData.lessonContent,
        matchData: data?.matchData ?? defaultRichMatchingData.matchData,
    };

    const playerView: PlayerViewLayout = config?.layout?.playerView ?? 'stacked';
    const matchCount = safeData.matchData?.items?.length ?? 0;
    const hasLessonContent = (safeData.lessonContent?.sections?.length ?? 0) > 0;
    const hasRootDescription = Boolean(safeData.description?.trim());

    const instructionsHeader = hasRootDescription ? (
        <div className="rich-matching-player-header rich-matching-player-header--intro flex-shrink-0 space-y-2">
            <p className="rich-matching-player-description text-sm text-muted-foreground leading-relaxed">
                {safeData.description}
            </p>
        </div>
    ) : null;

    const lessonPanel = (
        <PagePlayer data={safeData.lessonContent} submitted={showFeedback && revealCorrect} />
    );

    const questionsPanel = (
        <MatchPlayer
            data={safeData.matchData}
            answer={answer ?? {}}
            onAnswer={onAnswer}
            isReview={isReview}
            disabled={disabled}
            showFeedback={showFeedback}
            result={result}
            config={config as Record<string, unknown>}
        />
    );

    if (playerView === 'stacked') {
        return (
            <div className={cn('rich-matching-player rich-matching-player--stacked flex flex-col gap-6', className)}>
                {instructionsHeader}
                {hasLessonContent && (
                    <div className="rich-matching-player-lesson">
                        <div className="rich-matching-player-lesson-header flex items-center gap-2 pb-3 border-b mb-3">
                            <BookOpen className="h-5 w-5 text-primary" />
                            <h3 className="font-semibold">{t('lessonTab')}</h3>
                        </div>
                        {lessonPanel}
                    </div>
                )}
                <div className="rich-matching-player-questions">
                    <div className="rich-matching-player-questions-header flex items-center gap-2 pb-3 border-b mb-3">
                        <Shuffle className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('questionsTab')} ({matchCount})</h3>
                    </div>
                    {questionsPanel}
                </div>
            </div>
        );
    }

    if (playerView === 'split' && hasLessonContent) {
        return (
            <div className={cn('rich-matching-player rich-matching-player--split flex flex-col min-h-0 flex-1 gap-6', className)}>
                {instructionsHeader}
                <ContentPlayerSplitLayout
                    prefix="rich-matching-player-split"
                    splitRatio={config?.layout?.splitRatio ?? '50-50'}
                    leftPanel={
                        <div className="rich-matching-player-lesson flex flex-col min-h-0">
                            <div className="rich-matching-player-lesson-header flex items-center gap-2 pb-3 border-b mb-3">
                                <BookOpen className="h-5 w-5 text-primary" />
                                <h3 className="font-semibold">{t('lessonTab')}</h3>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {lessonPanel}
                            </div>
                        </div>
                    }
                    rightPanel={
                        <div className="rich-matching-player-questions flex flex-col min-h-0">
                            <div className="rich-matching-player-questions-header flex items-center gap-2 pb-3 border-b mb-3">
                                <Shuffle className="h-5 w-5 text-primary" />
                                <h3 className="font-semibold">{t('questionsTab')} ({matchCount})</h3>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {questionsPanel}
                            </div>
                        </div>
                    }
                />
            </div>
        );
    }

    if (playerView === 'split' && !hasLessonContent) {
        return (
            <div className={cn('rich-matching-player rich-matching-player--split flex flex-col gap-6', className)}>
                {instructionsHeader}
                <div className="rich-matching-player-questions flex flex-col min-h-0">
                    <div className="rich-matching-player-questions-header flex items-center gap-2 pb-3 border-b mb-3">
                        <Shuffle className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('questionsTab')} ({matchCount})</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {questionsPanel}
                    </div>
                </div>
            </div>
        );
    }

    // Tabs view: when lesson empty, show only questions (no tabs)
    if (playerView === 'tabs' && !hasLessonContent) {
        return (
            <div className={cn('rich-matching-player rich-matching-player--tabs flex flex-col gap-6', className)}>
                {instructionsHeader}
                <div className="rich-matching-player-questions flex flex-col min-h-0">
                    <div className="rich-matching-player-questions-header flex items-center gap-2 pb-3 border-b mb-3">
                        <Shuffle className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">{t('questionsTab')} ({matchCount})</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto">{questionsPanel}</div>
                </div>
            </div>
        );
    }

    const tabs = [
        { value: 'lesson', label: t('lessonTab'), icon: BookOpen },
        { value: 'questions', label: t('questionsTab'), icon: Shuffle },
    ];

    return (
        <div className={cn('rich-matching-player rich-matching-player--tabs flex flex-col gap-6', className)}>
            {instructionsHeader}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
                <TabsList variant="primary" className="w-full h-auto px-1 py-1 mb-4">
                    {tabs
                        .filter((tab) => tab.value !== 'lesson' || hasLessonContent)
                        .map((tab) => {
                            const count = tab.value === 'questions' ? matchCount : undefined;
                            const Icon = tab.icon;
                            return (
                                <TabsTrigger
                                    key={tab.value}
                                    value={tab.value}
                                    className="rich-matching-player-tab flex-1 py-3 px-4 text-sm font-semibold"
                                >
                                    <Icon className="h-4 w-4" />
                                    <span>{tab.label}{count !== undefined ? ` (${count})` : ''}</span>
                                </TabsTrigger>
                            );
                        })}
                </TabsList>
                <TabsContent value="lesson" className="rich-matching-player-content flex-1 min-h-0 mt-0">
                    {lessonPanel}
                </TabsContent>
                <TabsContent value="questions" className="rich-matching-player-content flex-1 min-h-0 mt-0">
                    {questionsPanel}
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default RichMatchingPlayer;
